import { useContext, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Devider, Flex, Form, FormContext, InputNumber } from 'dash-ui';
import { useTranslation } from 'react-i18next';

import { FormFields } from 'containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/constants/constants';
import { or } from 'dash-common';

const getFormField = (field: string) => {
  switch (field) {
    case 'bedrooms':
      return FormFields.AverageBedroomsNumber;
    case 'bathrooms':
      return FormFields.AverageBathroomsNumber;
    case 'interiorSqft':
      return FormFields.AverageInteriorSqft;
    case 'exteriorSqft':
      return FormFields.AverageExteriorSqft;
    default:
      return FormFields.AverageBathroomsNumber;
  }
};

const AverageUnitValueInput = ({
  fieldKey,
  disabled,
}: {
  fieldKey: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('forms');
  const theme = useTheme();
  const { formErrors, stateHandlers, formState } = useContext(FormContext);
  const formField = getFormField(fieldKey);

  const handleValues = (key: string) => (value: string) => {
    stateHandlers[formField]({
      ...(formState[formField] || {}),
      [key]: or(isNaN(Number(value)), value === '') ? undefined : value,
    });
  };

  const placeholder = useMemo(() => {
    switch (fieldKey) {
      case 'bedrooms':
      case 'bathrooms':
        return 'averageBathroomsNumber';
      case 'interiorSqft':
      case 'exteriorSqft':
        return 'sqft';
      default:
        return 'averageBathroomsNumber';
    }
  }, [fieldKey]);

  const { minValue, maxValue } = useMemo(
    () => ({
      minValue: isNaN(formState[formField]?.min)
        ? null
        : formState[formField]?.min,
      maxValue: isNaN(formState[formField]?.max)
        ? null
        : formState[formField]?.max,
    }),
    [formState, formField]
  );

  return (
    <Flex flex={1} alignItems="center" flexDirection="column">
      <Flex alignItems="center">
        <Form.Group mt={4} w="100%">
          <Form.Label>
            {t(`labels.averageNumbersLabels.${fieldKey}`, {
              range: t('labels.min'),
            })}
          </Form.Label>
          <InputNumber
            onChangeValue={handleValues('min')}
            value={minValue}
            maskOptions={{
              prefix: '',
            }}
            placeholder={t(`placeholders.${placeholder}`) || ''}
            $errorType={formErrors[formField]?.type}
            disabled={disabled}
          />
        </Form.Group>
        <Devider
          orientation="horizontal"
          w={25}
          color={theme.colors.n70}
          mt={8}
          mx={2.5}
        />
        <Form.Group mt={4} w="100%">
          <Form.Label>
            {t(`labels.averageNumbersLabels.${fieldKey}`, {
              range: t('labels.max'),
            })}
          </Form.Label>
          <InputNumber
            onChangeValue={handleValues('max')}
            value={maxValue}
            maskOptions={{
              prefix: '',
            }}
            placeholder={t(`placeholders.${placeholder}`) || ''}
            $errorType={formErrors[formField]?.type}
            disabled={disabled}
          />
        </Form.Group>
      </Flex>
      <Flex>
        <Form.Feedback $errorType={formErrors[formField]?.type}>
          {formErrors[formField]?.message}
        </Form.Feedback>
      </Flex>
    </Flex>
  );
};

export default AverageUnitValueInput;
